import { useState, useRef, useEffect } from "react";
import { Send, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ActionCard } from "./ActionCard";
import { ChatMarkdown } from "./ChatMarkdown";
import { StandardPrompts } from "./StandardPrompts";
import { api, streamChat } from "@/api/client";
import { toast } from "sonner";

export function AiDock({ projectId, currentView, onModelChanged }) {
  const [messages, setMessages] = useState([]);
  const [pendingMap, setPendingMap] = useState({});
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [showPrompts, setShowPrompts] = useState(false);
  const scrollRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    Promise.all([api.get(`/projects/${projectId}/chat/messages`), api.get(`/projects/${projectId}/chat/actions/pending`)]).then(
      ([msgsRes, actionsRes]) => {
        const map = {};
        actionsRes.data.forEach((a) => {
          map[a.id] = a;
        });
        setPendingMap(map);
        setMessages(msgsRes.data);
      }
    );
  }, [projectId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const confirmAction = async (actionId) => {
    const res = await api.post(`/projects/${projectId}/chat/actions/${actionId}/confirm`, { view_id: currentView?.id });
    setPendingMap((m) => ({ ...m, [actionId]: { ...m[actionId], status: "confirmed" } }));
    setMessages((m) => [...m, res.data.message]);
    onModelChanged();
  };

  const rejectAction = async (actionId) => {
    const res = await api.post(`/projects/${projectId}/chat/actions/${actionId}/reject`);
    setPendingMap((m) => ({ ...m, [actionId]: { ...m[actionId], status: "rejected" } }));
    setMessages((m) => [...m, res.data.message]);
  };

  const send = async (overrideText) => {
    const text = (overrideText ?? input).trim();
    if (!text || streaming) return;
    setInput("");
    setShowPrompts(false);
    setMessages((m) => [...m, { id: `local-user-${Date.now()}`, role: "user", content: text, pending_actions: [] }]);
    setStreaming(true);
    let assistantContent = "";
    const localActions = [];
    const assistantId = `local-assistant-${Date.now()}`;
    setMessages((m) => [...m, { id: assistantId, role: "assistant", content: "", pending_actions: [], _actions: [] }]);
    try {
      for await (const ev of streamChat(projectId, text, currentView?.id, currentView?.name)) {
        if (ev.type === "text") {
          assistantContent += ev.content;
          setMessages((m) => m.map((msg) => (msg.id === assistantId ? { ...msg, content: assistantContent } : msg)));
        } else if (ev.type === "pending_action") {
          localActions.push(ev.action);
          setPendingMap((pm) => ({ ...pm, [ev.action.id]: ev.action }));
          setMessages((m) => m.map((msg) => (msg.id === assistantId ? { ...msg, _actions: [...localActions] } : msg)));
        } else if (ev.type === "final") {
          setMessages((m) => m.map((msg) => (msg.id === assistantId ? { ...msg, id: ev.message_id, content: ev.content } : msg)));
          onModelChanged();
        }
      }
    } catch (e) {
      toast.error("ModernTect Assistant is unavailable right now");
    } finally {
      setStreaming(false);
    }
  };

  const fillPrompt = (text) => {
    setInput(text);
    setShowPrompts(false);
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.focus();
      const pos = text.indexOf('""') + 1;
      el.setSelectionRange(pos, pos);
    });
  };

  return (
    <div className="flex h-full flex-col bg-slate-950 text-slate-100" data-testid="ai-copilot-dock">
      <div className="p-3 border-b border-slate-800 flex items-center gap-2">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-purple-500" />
        </span>
        <h2 className="text-sm font-semibold bg-gradient-to-r from-purple-300 to-sky-300 bg-clip-text text-transparent">ModernTect Assistant</h2>
      </div>
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3" data-testid="ai-chat-messages">
        {messages.length === 0 && (
          <div className="space-y-3">
            <div className="text-xs text-slate-500 leading-relaxed">
              Ask me about this architecture, or try one of these:
            </div>
            <StandardPrompts currentView={currentView} onSend={(text) => send(text)} onFill={fillPrompt} disabled={streaming} />
          </div>
        )}
        {messages.map((msg) => {
          const resolvedActions = msg._actions || (msg.pending_actions || []).map((id) => pendingMap[id]).filter(Boolean);
          return (
            <div key={msg.id} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[90%] rounded-lg px-3 py-2 text-sm ${
                  msg.role === "user"
                    ? "bg-slate-700 text-slate-100"
                    : msg.role === "system"
                    ? "bg-transparent text-slate-500 text-xs italic px-0"
                    : "bg-slate-900 border border-purple-500/30 text-slate-100"
                }`}
                data-testid={`chat-message-${msg.role}`}
              >
                <div className={msg.role === "user" ? "whitespace-pre-wrap" : ""}>
                  {msg.role === "user" ? (
                    msg.content
                  ) : msg.content ? (
                    <ChatMarkdown content={msg.content} />
                  ) : streaming && msg.role === "assistant" ? (
                    "\u2026"
                  ) : (
                    ""
                  )}
                </div>
                {resolvedActions.map((a) => (
                  <ActionCard key={a.id} action={a} onConfirm={confirmAction} onReject={rejectAction} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <div className="p-3 border-t border-slate-800 space-y-2">
        {messages.length > 0 && (
          <div>
            <button
              data-testid="toggle-suggested-prompts-button"
              onClick={() => setShowPrompts((v) => !v)}
              className="flex items-center gap-1 text-[11px] text-slate-400 hover:text-slate-200 transition-colors"
            >
              <Sparkles size={11} />
              Suggested prompts
            </button>
            {showPrompts && (
              <StandardPrompts
                currentView={currentView}
                onSend={(text) => send(text)}
                onFill={fillPrompt}
                disabled={streaming}
                className="mt-2"
              />
            )}
          </div>
        )}
        <div className="flex gap-2">
          <Textarea
            ref={textareaRef}
            data-testid="ai-chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder="Ask about your architecture, or ask me to build something..."
            className="bg-slate-900 border-slate-700 text-slate-100 placeholder:text-slate-500 resize-none text-sm min-h-[44px]"
          />
          <Button data-testid="ai-chat-send-button" onClick={() => send()} disabled={streaming} className="bg-purple-600 hover:bg-purple-500 shrink-0">
            <Send size={16} />
          </Button>
        </div>
      </div>
    </div>
  );
}
